/**
 * EVERY SOUND IN THE GAME, MADE OUT OF MATHS.
 *
 * There is not one audio file in this project and there is not going to be.
 * That is a deliberate choice rather than a shortcut, and it is worth being
 * explicit about why, because "just download some samples" is the obvious
 * alternative and it is wrong for this build in four separate ways.
 *
 * It ships nothing. A decent footstep set is a few megabytes; a full mall's
 * worth of doors, tools, alarms and ambience is tens. This game loads over the
 * web from a static host and its entire current payload is code.
 *
 * It is licensable. Freesound is a minefield of attribution clauses, and a
 * Steam release that has to ship a credits file naming forty uploaders is a
 * release that has to keep track of forty uploaders.
 *
 * It is parametric, which is the real reason. A footstep is not a sound, it is
 * a function of a surface, a weight, a speed and a boot. Sampled, that is a
 * grid of forty files and a random picker that still sounds like a grid of
 * forty files. Synthesised, walking gets quieter as you slow down, brighter on
 * tile than on asphalt, heavier when you are carrying sixty kilos of jewellery,
 * and every step is different because the noise seed moved on.
 *
 * And it is testable. Every cue here is a pure function of (context, output
 * node, start time) — nothing reads a global clock and nothing touches the live
 * graph. That means `tools/listen.ts` can render each one into an
 * OfflineAudioContext and measure it, which matters more than it sounds like it
 * should: I cannot hear any of this. The same discipline that put a histogram
 * behind every screenshot puts peak, RMS, duration and spectral centroid behind
 * every sound, and a cue that is silent, clipped, three times too long, or a
 * duplicate of its neighbour is caught by a number rather than by a player.
 */

export type FloorSound = 'tile' | 'tileDark' | 'concrete' | 'asphalt' | 'checker' | 'none';

/** What holding the interact key sounds like. Set by the interaction layer. */
export type WorkKind =
  | 'none'
  | 'take'
  | 'force'
  | 'pick'
  | 'card'
  | 'cut'
  | 'safe'
  | 'deposit'
  | 'leave';

export interface CueCtx {
  ctx: BaseAudioContext;
  /** Where this cue's output goes. A panner, or the head bus. */
  out: AudioNode;
  /** Context time to start at. Never `currentTime` inside a cue — see the note. */
  t: number;
  /** Seeded, so an offline render of a cue is byte-identical run to run. */
  rng: () => number;
}

// ---------------------------------------------------------------------------
// plumbing
// ---------------------------------------------------------------------------

/** Mulberry32. Small, fast, and repeatable, which is the only property wanted. */
export function seeded(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/**
 * White noise, cached per (context sample rate, length, seed).
 *
 * Buffers are shared across contexts freely, but only ones built at the same
 * sample rate: a 48k buffer played in a 44.1k context is pitched down by a
 * tenth of an octave, which is inaudible on noise and very audible on the tonal
 * cues that use noise for their attack. Keyed on the rate, so the offline
 * harness and the live context can never hand each other the wrong one.
 */
const noiseCache = new Map<string, AudioBuffer>();
export function noiseBuffer(ctx: BaseAudioContext, seconds: number, seed = 1): AudioBuffer {
  const n = Math.max(1, Math.ceil(seconds * ctx.sampleRate));
  const key = `${ctx.sampleRate}:${n}:${seed}`;
  const hit = noiseCache.get(key);
  if (hit) return hit;
  const buf = ctx.createBuffer(1, n, ctx.sampleRate);
  const d = buf.getChannelData(0);
  const rnd = seeded(seed);
  for (let i = 0; i < n; i++) d[i] = rnd() * 2 - 1;
  noiseCache.set(key, buf);
  return buf;
}

/**
 * A burst of noise with its own gain stage, already scheduled to stop.
 *
 * Every one-shot in this file is built out of this plus a filter plus an
 * envelope. Source nodes are single-use by specification, so there is no pooling
 * to be had here — the allocation is the API.
 */
function noise(c: CueCtx, seconds: number): { src: AudioBufferSourceNode; gain: GainNode } {
  const src = c.ctx.createBufferSource();
  // A whole second of source material, played from a random offset, so two
  // footsteps a tenth of a second apart are not the same eighty milliseconds of
  // noise twice. This is the entire trick behind synthesised steps not sounding
  // like a loop.
  src.buffer = noiseBuffer(c.ctx, 1.0, 1);
  const gain = c.ctx.createGain();
  gain.gain.value = 0;
  src.connect(gain);
  src.start(c.t, c.rng() * 0.9, seconds + 0.02);
  src.stop(c.t + seconds + 0.05);
  return { src, gain };
}

function osc(c: CueCtx, type: OscillatorType, freq: number, seconds: number): { o: OscillatorNode; gain: GainNode } {
  const o = c.ctx.createOscillator();
  o.type = type;
  o.frequency.value = freq;
  const gain = c.ctx.createGain();
  gain.gain.value = 0;
  o.connect(gain);
  o.start(c.t);
  o.stop(c.t + seconds + 0.05);
  return { o, gain };
}

function filter(c: CueCtx, type: BiquadFilterType, freq: number, q = 1): BiquadFilterNode {
  const f = c.ctx.createBiquadFilter();
  f.type = type;
  f.frequency.value = freq;
  f.Q.value = q;
  return f;
}

/**
 * A bandpass that actually band-passes.
 *
 * This exists because of a measurement. Every noise cue in this file was
 * authored as "a bandpass at the frequency this thing sounds like", and every
 * single one rendered a full octave brighter than that number — a footstep on
 * asphalt and a footstep on polished tile came out four percent apart, and the
 * whole surface table might as well not have been written.
 *
 * The cause is arithmetic, not taste. A biquad bandpass rolls off at six
 * decibels per octave, and white noise carries equal energy per hertz. Above the
 * centre frequency there are tens of thousands of hertz of gently-attenuated
 * skirt; below it there are only a few thousand. The skirt wins on total energy
 * every time, so what the ear gets is hiss with a hint of the intended peak.
 *
 * A second-order rolloff on top costs one biquad and makes the centre frequency
 * mean what it says. Connect into `bp`, take the output from `cap`.
 */
function band(c: CueCtx, freq: number, q = 1, capMul = 2.0): { bp: BiquadFilterNode; cap: BiquadFilterNode } {
  const bp = filter(c, 'bandpass', freq, q);
  const cap = filter(c, 'lowpass', freq * capMul, 0.7);
  bp.connect(cap);
  return { bp, cap };
}

/**
 * Attack/decay on a gain, expressed the way a sound designer thinks about it.
 *
 * Exponential decay rather than linear, because a linear fade to zero on a
 * percussive sound is heard as a click at the end of the ramp — the ear reads
 * amplitude logarithmically and a straight line is a sharp corner. Web Audio's
 * exponential ramp cannot reach zero, hence the tiny floor and the linear
 * cleanup after it.
 */
function hit(g: AudioParam, t: number, peak: number, attack: number, decay: number): number {
  g.setValueAtTime(0.0001, t);
  g.exponentialRampToValueAtTime(Math.max(0.0002, peak), t + attack);
  g.exponentialRampToValueAtTime(0.0001, t + attack + decay);
  g.linearRampToValueAtTime(0, t + attack + decay + 0.01);
  return attack + decay + 0.01;
}

// ---------------------------------------------------------------------------
// footsteps
// ---------------------------------------------------------------------------

/**
 * The floor, as four numbers.
 *
 * `bright` is where the contact transient sits, `q` how resonant that contact
 * is, `body` how much low thud comes with it, and `grit` how long the noise
 * tail runs on for. Polished mall tile is a bright, ringing tick with almost no
 * tail; the loading dock's asphalt is a dull scuff with a long one. Those two
 * lines are the whole difference between being inside the shops and being out
 * the back, and the player learns it without being told.
 */
const FLOOR: Record<FloorSound, { bright: number; q: number; body: number; grit: number }> = {
  tile: { bright: 3400, q: 1.7, body: 0.34, grit: 0.055 },
  checker: { bright: 3100, q: 1.6, body: 0.36, grit: 0.06 },
  tileDark: { bright: 2500, q: 1.3, body: 0.42, grit: 0.07 },
  concrete: { bright: 1500, q: 0.9, body: 0.55, grit: 0.09 },
  asphalt: { bright: 950, q: 0.7, body: 0.5, grit: 0.13 },
  none: { bright: 1500, q: 0.9, body: 0.5, grit: 0.09 },
};

export interface StepOpts {
  surface?: FloorSound;
  /** 0..1. A crouched creep and a loaded sprint are the two ends of this. */
  effort?: number;
  /** Guard boots are heavier and slower than a thief's trainers. */
  boot?: boolean;
}

export function step(c: CueCtx, o: StepOpts = {}): number {
  const f = FLOOR[o.surface ?? 'tile'];
  const effort = Math.max(0.05, Math.min(1, o.effort ?? 0.6));
  const boot = o.boot ?? false;
  // Detune the whole step a little per footfall. Two identical steps in a row is
  // the single loudest tell that a sound is synthetic, and ±12% is enough to
  // break it without the player hearing a pitch wobble.
  const vary = 0.88 + c.rng() * 0.24;

  const dur = f.grit * (boot ? 1.35 : 1) * vary;
  const n = noise(c, dur + 0.05);
  const centre = f.bright * vary * (boot ? 0.8 : 1);
  const contact = band(c, centre, f.q, 1.9);
  n.gain.connect(contact.bp);
  contact.cap.connect(c.out);
  // A second, lower band. One filter reads as a click; two read as a shoe.
  const lo = filter(c, 'lowpass', 700 * vary, 0.9);
  n.gain.connect(lo);
  lo.connect(c.out);
  const tail = hit(n.gain.gain, c.t, 0.5 * effort * (boot ? 1.15 : 1), 0.0025, dur);

  // The body: a very short pitch-dropping sine. This is the weight of the
  // person, and it is why a guard walking towards you is felt before it is
  // heard through a wall — the low end is what survives the occlusion filter.
  const b = osc(c, 'sine', 132 * vary * (boot ? 0.78 : 1), 0.14);
  b.o.frequency.exponentialRampToValueAtTime(52 * vary, c.t + 0.09);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, f.body * effort * (boot ? 1.4 : 1), 0.004, 0.085);

  return Math.max(tail, 0.1);
}

/** Landing. A step with the weight turned up and the transient widened. */
export function land(c: CueCtx, o: StepOpts = {}): number {
  const a = step(c, { ...o, effort: 1 });
  const b = osc(c, 'sine', 88, 0.24);
  b.o.frequency.exponentialRampToValueAtTime(38, c.t + 0.16);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, 0.5, 0.006, 0.19);
  return Math.max(a, 0.22);
}

/** The exhale and the jacket. Not a foley detail — it is the jump's only tell. */
export function cloth(c: CueCtx, amount = 1): number {
  const n = noise(c, 0.22);
  const b = band(c, 1800 + c.rng() * 900, 0.7, 2.2);
  n.gain.connect(b.bp);
  b.cap.connect(c.out);
  return hit(n.gain.gain, c.t, 0.13 * amount, 0.02, 0.17);
}

// ---------------------------------------------------------------------------
// hands
// ---------------------------------------------------------------------------

/** Something light off a shelf: a scuff and a small tick. */
export function take(c: CueCtx): number {
  const n = noise(c, 0.14);
  const b = band(c, 2400 + c.rng() * 1200, 1.1, 1.8);
  n.gain.connect(b.bp);
  b.cap.connect(c.out);
  hit(n.gain.gain, c.t, 0.3, 0.006, 0.11);
  const t = osc(c, 'triangle', 900 + c.rng() * 300, 0.06);
  t.gain.connect(c.out);
  hit(t.gain.gain, c.t + 0.03, 0.08, 0.003, 0.04);
  return 0.2;
}

/** Something heavy onto the floor. */
export function drop(c: CueCtx): number {
  const b = osc(c, 'sine', 150, 0.2);
  b.o.frequency.exponentialRampToValueAtTime(58, c.t + 0.12);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, 0.42, 0.004, 0.15);
  const n = noise(c, 0.16);
  const thud = band(c, 1100, 0.8, 2.0);
  n.gain.connect(thud.bp);
  thud.cap.connect(c.out);
  hit(n.gain.gain, c.t, 0.2, 0.004, 0.12);
  return 0.22;
}

// ---------------------------------------------------------------------------
// the building
// ---------------------------------------------------------------------------

/**
 * A lock giving way. The payoff at the end of eight seconds of grinder, so it
 * is allowed to be the loudest thing in the game that is not an alarm.
 */
export function breach(c: CueCtx): number {
  // The bolt.
  const b = osc(c, 'square', 220, 0.1);
  b.o.frequency.exponentialRampToValueAtTime(70, c.t + 0.06);
  const lp = filter(c, 'lowpass', 1400, 1.2);
  b.gain.connect(lp);
  lp.connect(c.out);
  hit(b.gain.gain, c.t, 0.36, 0.002, 0.09);
  // Metal, ringing off. Three inharmonic partials — a harmonic stack sounds
  // like a bell, and this is supposed to sound like a cabinet.
  for (const [f, g, d] of [
    [1870, 0.12, 0.5],
    [2630, 0.09, 0.38],
    [3910, 0.06, 0.26],
  ]) {
    const p = osc(c, 'sine', f * (0.97 + c.rng() * 0.06), d + 0.1);
    p.gain.connect(c.out);
    hit(p.gain.gain, c.t + 0.005, g, 0.004, d);
  }
  const n = noise(c, 0.3);
  const hp = filter(c, 'highpass', 2200, 0.7);
  // Capped at 6k. Unbounded highpassed noise measures as a 10kHz hiss, which is
  // the sound of a cymbal, not of a lock failing.
  const cap = filter(c, 'lowpass', 6000, 0.7);
  n.gain.connect(hp);
  hp.connect(cap);
  cap.connect(c.out);
  hit(n.gain.gain, c.t, 0.16, 0.002, 0.22);
  return 0.65;
}

/** A roller shutter going up. Long, rattly, and audible across the atrium. */
export function shutter(c: CueCtx): number {
  const dur = 1.4;
  const n = noise(c, dur);
  const rail = band(c, 1250, 1.6, 2.1);
  const bp = rail.bp;
  n.gain.connect(bp);
  rail.cap.connect(c.out);
  // Rattle: the band sweeps up as the shutter accelerates, then the whole thing
  // is chopped by a fast tremolo, which is what slats passing a guide rail are.
  bp.frequency.setValueAtTime(700, c.t);
  bp.frequency.linearRampToValueAtTime(1900, c.t + dur * 0.6);
  bp.frequency.linearRampToValueAtTime(900, c.t + dur);
  const g = n.gain.gain;
  g.setValueAtTime(0.0001, c.t);
  g.linearRampToValueAtTime(0.3, c.t + 0.08);
  const chop = c.ctx.createOscillator();
  chop.type = 'square';
  chop.frequency.setValueAtTime(17, c.t);
  chop.frequency.linearRampToValueAtTime(31, c.t + dur * 0.7);
  const chopDepth = c.ctx.createGain();
  chopDepth.gain.value = 0.16;
  chop.connect(chopDepth);
  chopDepth.connect(g);
  chop.start(c.t);
  chop.stop(c.t + dur + 0.05);
  g.linearRampToValueAtTime(0.22, c.t + dur * 0.8);
  g.linearRampToValueAtTime(0, c.t + dur);
  // And the stop at the top.
  const clunk = osc(c, 'sine', 120, 0.2);
  clunk.o.frequency.exponentialRampToValueAtTime(48, c.t + dur + 0.1);
  clunk.gain.connect(c.out);
  hit(clunk.gain.gain, c.t + dur, 0.3, 0.003, 0.16);
  return dur + 0.3;
}

/** The chute. A bag going down a steel throat and landing somewhere below. */
export function chute(c: CueCtx): number {
  const n = noise(c, 0.9);
  const duct = band(c, 900, 2.2, 2.4);
  const bp = duct.bp;
  n.gain.connect(bp);
  duct.cap.connect(c.out);
  // Falling: the resonance drops as the bag gets further away down the duct.
  bp.frequency.setValueAtTime(1700, c.t);
  bp.frequency.exponentialRampToValueAtTime(320, c.t + 0.75);
  const g = n.gain.gain;
  g.setValueAtTime(0.0001, c.t);
  g.linearRampToValueAtTime(0.34, c.t + 0.05);
  g.exponentialRampToValueAtTime(0.06, c.t + 0.7);
  g.linearRampToValueAtTime(0, c.t + 0.8);
  const thud = osc(c, 'sine', 90, 0.3);
  thud.o.frequency.exponentialRampToValueAtTime(40, c.t + 0.9);
  thud.gain.connect(c.out);
  hit(thud.gain.gain, c.t + 0.78, 0.34, 0.004, 0.22);
  return 1.1;
}

/**
 * Money landing. The one unambiguously good sound in the game.
 *
 * A rising major triad, short, with the register bell on top. It is deliberately
 * the only tonal, consonant, in-tune thing in the whole mix — everything else is
 * noise, metal and inharmonic partials — so that banking a haul cuts through a
 * chase without needing to be loud.
 */
export function bank(c: CueCtx, scale = 1): number {
  const root = 523.25; // C5
  const notes = [root, root * 1.25, root * 1.5, root * 2];
  notes.forEach((f, i) => {
    const t0 = c.t + i * 0.055;
    const o = c.ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = f;
    const g = c.ctx.createGain();
    g.gain.value = 0;
    o.connect(g);
    g.connect(c.out);
    o.start(t0);
    o.stop(t0 + 0.6);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.16 * scale, t0 + 0.008);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.42);
    g.gain.linearRampToValueAtTime(0, t0 + 0.45);
  });
  // The register bell, as a narrow band rather than an open highpass: the triad
  // is the message and the shimmer is punctuation, and an unbounded highpass
  // puts more energy above 8kHz than the notes have in total.
  const n = noise(c, 0.2);
  const hp = filter(c, 'bandpass', 5200, 1.4);
  n.gain.connect(hp);
  hp.connect(c.out);
  hit(n.gain.gain, c.t, 0.05 * scale, 0.003, 0.14);
  return 0.7;
}

// ---------------------------------------------------------------------------
// trouble
// ---------------------------------------------------------------------------

/**
 * Spotted. Not an alarm — the moment the meter starts filling.
 *
 * Two notes a tritone apart, the second below the first. It has to be legible
 * at the exact instant it fires, because the player's response to it is to stop
 * moving, and any sound that takes half a second to identify has already cost
 * them the half second that mattered.
 */
export function spotted(c: CueCtx): number {
  const pair = [740, 523.25];
  pair.forEach((f, i) => {
    const t0 = c.t + i * 0.1;
    const o = c.ctx.createOscillator();
    o.type = 'sawtooth';
    o.frequency.value = f;
    const g = c.ctx.createGain();
    g.gain.value = 0;
    const lp = c.ctx.createBiquadFilter();
    lp.type = 'lowpass';
    lp.frequency.value = 2400;
    lp.Q.value = 1.4;
    o.connect(g);
    g.connect(lp);
    lp.connect(c.out);
    o.start(t0);
    o.stop(t0 + 0.5);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.2, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.34);
    g.gain.linearRampToValueAtTime(0, t0 + 0.36);
  });
  return 0.55;
}

/** Caught. The round ending in your face. */
export function caught(c: CueCtx): number {
  const o = c.ctx.createOscillator();
  o.type = 'sawtooth';
  o.frequency.setValueAtTime(180, c.t);
  o.frequency.exponentialRampToValueAtTime(42, c.t + 1.1);
  const lp = c.ctx.createBiquadFilter();
  lp.type = 'lowpass';
  // Starts at 800, not 1800. A saw through an open filter is bright enough to
  // measure as the alarm, and the two must never be mistaken for each other:
  // one means run and the other means it is over.
  lp.frequency.setValueAtTime(800, c.t);
  lp.frequency.exponentialRampToValueAtTime(160, c.t + 1.1);
  const g = c.ctx.createGain();
  g.gain.value = 0;
  o.connect(lp);
  lp.connect(g);
  g.connect(c.out);
  o.start(c.t);
  o.stop(c.t + 1.4);
  g.gain.setValueAtTime(0.0001, c.t);
  g.gain.exponentialRampToValueAtTime(0.3, c.t + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, c.t + 1.15);
  g.gain.linearRampToValueAtTime(0, c.t + 1.2);
  const n = noise(c, 0.5);
  const b = band(c, 600, 0.6, 1.8);
  n.gain.connect(b.bp);
  b.cap.connect(c.out);
  hit(n.gain.gain, c.t, 0.18, 0.004, 0.4);
  return 1.4;
}

/** One cycle of the mall alarm. Repeated by the director for as long as it runs. */
export function alarm(c: CueCtx): number {
  const dur = 1.0;
  const o = c.ctx.createOscillator();
  o.type = 'square';
  // Two-tone rather than a siren sweep: a sweep reads as a car outside, and a
  // two-tone warble reads as a building telling you to leave it.
  o.frequency.setValueAtTime(660, c.t);
  o.frequency.setValueAtTime(880, c.t + 0.25);
  o.frequency.setValueAtTime(660, c.t + 0.5);
  o.frequency.setValueAtTime(880, c.t + 0.75);
  const lp = filter(c, 'lowpass', 2600, 1.1);
  const g = c.ctx.createGain();
  g.gain.value = 0;
  o.connect(lp);
  lp.connect(g);
  g.connect(c.out);
  o.start(c.t);
  o.stop(c.t + dur);
  g.gain.setValueAtTime(0.0001, c.t);
  g.gain.linearRampToValueAtTime(0.17, c.t + 0.03);
  g.gain.setValueAtTime(0.17, c.t + dur - 0.12);
  g.gain.linearRampToValueAtTime(0, c.t + dur - 0.02);
  return dur;
}

/**
 * A guard on the radio.
 *
 * No words, and that is on purpose — recorded lines would need a voice actor
 * and would repeat within one round. What carries the information is the shape:
 * squelch, a burst of syllables, squelch. The player hears "someone just said
 * something about you three rooms away" and looks at the toast for the rest.
 *
 * The syllables are bandpassed noise through two formant peaks, gated by an
 * envelope that steps every 90 to 160 milliseconds. Speech is, to a first
 * approximation, exactly that.
 */
export function radio(c: CueCtx, seconds = 1.1): number {
  const squelch = (t: number, level: number) => {
    const src = c.ctx.createBufferSource();
    src.buffer = noiseBuffer(c.ctx, 1.0, 7);
    const hp = filter(c, 'highpass', 2600, 0.9);
    const squelchCap = filter(c, 'lowpass', 5200, 0.7);
    const g = c.ctx.createGain();
    g.gain.value = 0;
    src.connect(hp);
    hp.connect(squelchCap);
    squelchCap.connect(g);
    g.connect(c.out);
    src.start(t, c.rng() * 0.9, 0.09);
    src.stop(t + 0.1);
    hit(g.gain, t, level, 0.002, 0.055);
  };

  squelch(c.t, 0.14);

  const src = c.ctx.createBufferSource();
  src.buffer = noiseBuffer(c.ctx, 1.0, 3);
  src.loop = true;
  const band = filter(c, 'bandpass', 1500, 0.9);
  // 300Hz to 3.4kHz is the actual passband of a handheld radio, and imposing it
  // is both free realism and the fix for a cue that otherwise measures as a
  // 7kHz hiss with a mouth somewhere inside it.
  const voiceBand = filter(c, 'lowpass', 3400, 0.9);
  const f1 = filter(c, 'peaking', 640, 6);
  f1.gain.value = 14;
  const f2 = filter(c, 'peaking', 1900, 7);
  f2.gain.value = 11;
  const g = c.ctx.createGain();
  g.gain.value = 0;
  src.connect(band);
  band.connect(f1);
  f1.connect(f2);
  f2.connect(voiceBand);
  voiceBand.connect(g);
  g.connect(c.out);
  const t0 = c.t + 0.06;
  src.start(t0, c.rng() * 0.9);
  src.stop(t0 + seconds + 0.1);

  let t = t0;
  const end = t0 + seconds;
  g.gain.setValueAtTime(0.0001, t);
  while (t < end) {
    const syl = 0.09 + c.rng() * 0.07;
    const loud = 0.05 + c.rng() * 0.075;
    // Move the first formant per syllable. Fixed formants read as a buzz; moving
    // ones read as a mouth.
    f1.frequency.setValueAtTime(430 + c.rng() * 460, t);
    f2.frequency.setValueAtTime(1500 + c.rng() * 900, t);
    g.gain.exponentialRampToValueAtTime(loud, t + syl * 0.35);
    g.gain.exponentialRampToValueAtTime(0.006, t + syl);
    t += syl + c.rng() * 0.045;
  }
  g.gain.linearRampToValueAtTime(0, end + 0.04);
  squelch(end + 0.05, 0.1);
  return seconds + 0.25;
}

/** A guard hitting the floor. */
export function guardDown(c: CueCtx): number {
  const b = osc(c, 'sine', 110, 0.4);
  b.o.frequency.exponentialRampToValueAtTime(36, c.t + 0.25);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, 0.5, 0.005, 0.3);
  const n = noise(c, 0.35);
  const lp = filter(c, 'lowpass', 900, 0.8);
  n.gain.connect(lp);
  lp.connect(c.out);
  hit(n.gain.gain, c.t, 0.26, 0.004, 0.28);
  // The radio, dropped, keying open for a moment on the way down.
  radio({ ...c, t: c.t + 0.25 }, 0.35);
  return 0.8;
}

/** The shove itself: an impact with no ring, so it reads as a body not a door. */
export function shove(c: CueCtx): number {
  const n = noise(c, 0.16);
  const lp = filter(c, 'lowpass', 1200, 0.9);
  n.gain.connect(lp);
  lp.connect(c.out);
  hit(n.gain.gain, c.t, 0.3, 0.003, 0.13);
  const b = osc(c, 'sine', 190, 0.16);
  b.o.frequency.exponentialRampToValueAtTime(75, c.t + 0.09);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, 0.28, 0.003, 0.11);
  return 0.22;
}

// ---------------------------------------------------------------------------
// the round
// ---------------------------------------------------------------------------

/** Doors unlocking at the top of the round. Clock's running. */
export function doorsOpen(c: CueCtx): number {
  const b = osc(c, 'sine', 70, 0.9);
  b.gain.connect(c.out);
  hit(b.gain.gain, c.t, 0.28, 0.02, 0.8);
  const n = noise(c, 0.9);
  const bp = filter(c, 'bandpass', 500, 1.2);
  bp.frequency.exponentialRampToValueAtTime(1600, c.t + 0.7);
  n.gain.connect(bp);
  bp.connect(c.out);
  hit(n.gain.gain, c.t, 0.14, 0.05, 0.75);
  return 1.0;
}

/** One minute left. A clock tick with a body, once per second, from the HUD. */
export function tick(c: CueCtx, urgent = false): number {
  const f = urgent ? 1400 : 1000;
  const t = osc(c, 'square', f, 0.06);
  // Centred ON the fundamental. It was an octave above it, with a Q of 3, which
  // put the note in the filter's stopband and left a cue thirty-five decibels
  // down — inaudible under the room tone, for the sound whose whole job is to
  // tell you the round is nearly over.
  const bp = filter(c, 'bandpass', f, 1.6);
  t.gain.connect(bp);
  bp.connect(c.out);
  return hit(t.gain.gain, c.t, urgent ? 0.34 : 0.2, 0.002, 0.05);
}

/** The round is over. A long fall, and then nothing. */
export function roundEnd(c: CueCtx): number {
  [220, 174.6, 130.8].forEach((f, i) => {
    const t0 = c.t + i * 0.42;
    const o = c.ctx.createOscillator();
    o.type = 'triangle';
    o.frequency.value = f;
    const g = c.ctx.createGain();
    g.gain.value = 0;
    o.connect(g);
    g.connect(c.out);
    o.start(t0);
    o.stop(t0 + 1.4);
    g.gain.setValueAtTime(0.0001, t0);
    g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.05);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + 1.2);
    g.gain.linearRampToValueAtTime(0, t0 + 1.3);
  });
  return 2.2;
}

/** A soft click for a HUD event that is not worth a sound but is worth a nudge. */
export function uiTick(c: CueCtx): number {
  const t = osc(c, 'sine', 1650, 0.05);
  t.gain.connect(c.out);
  return hit(t.gain.gain, c.t, 0.09, 0.001, 0.035);
}

/** Refused. The one negative UI sound; deliberately dull rather than harsh. */
export function refused(c: CueCtx): number {
  const t = osc(c, 'square', 180, 0.16);
  const lp = filter(c, 'lowpass', 700, 1);
  t.gain.connect(lp);
  lp.connect(c.out);
  return hit(t.gain.gain, c.t, 0.13, 0.004, 0.13);
}

// ---------------------------------------------------------------------------
// loops
// ---------------------------------------------------------------------------

/**
 * A running sound with a handle, for anything that lasts as long as you hold a
 * key or as long as you stand in a room.
 *
 * `stop` fades rather than cuts. A grinder that stops on the sample is a click,
 * and a click at the end of every breach is the kind of thing that is invisible
 * in isolation and exhausting over a forty-minute session.
 */
export interface Loop {
  gain: GainNode;
  stop(at: number, fade?: number): void;
  /** 0..1 progress through the work, for loops that build. */
  set(progress: number): void;
}

function loopHandle(ctx: BaseAudioContext, gain: GainNode, sources: AudioScheduledSourceNode[], set?: (p: number) => void): Loop {
  return {
    gain,
    stop(at: number, fade = 0.08) {
      gain.gain.cancelScheduledValues(at);
      gain.gain.setValueAtTime(Math.max(0.0001, gain.gain.value), at);
      gain.gain.linearRampToValueAtTime(0, at + fade);
      for (const s of sources) {
        try {
          s.stop(at + fade + 0.02);
        } catch {
          // Already stopped. Web Audio throws rather than no-ops, and there is
          // nothing to do about a source that has already done what was asked.
        }
      }
      void ctx;
    },
    set: set ?? (() => {}),
  };
}

/**
 * Tool work, one function, five characters.
 *
 * Each of these is a noise source and an oscillator through a filter, and the
 * differences between them are entirely in the numbers — which is the argument
 * for synthesis in one paragraph. The grinder is a loud, bright, wobbling
 * sawtooth; the lockpick is the same architecture with the oscillator muted and
 * the noise chopped into ticks. Both took ten lines.
 */
export function work(c: CueCtx, kind: WorkKind): Loop | null {
  if (kind === 'none' || kind === 'take' || kind === 'leave') return null;

  const out = c.ctx.createGain();
  out.gain.value = 0;
  out.connect(c.out);
  const sources: AudioScheduledSourceNode[] = [];
  const src = c.ctx.createBufferSource();
  src.buffer = noiseBuffer(c.ctx, 1.0, 5);
  src.loop = true;
  src.start(c.t, c.rng() * 0.9);
  sources.push(src);

  let setter: ((p: number) => void) | undefined;

  if (kind === 'cut') {
    // Angle grinder. A sawtooth an octave apart from itself, hard bandpass, and
    // the noise on top is the wheel biting. The pitch sags under load, which is
    // the detail that makes it read as cutting something rather than idling.
    const body = c.ctx.createGain();
    body.gain.value = 0.95;
    for (const f of [ 173, 349 ]) {
      const o = c.ctx.createOscillator();
      o.type = 'sawtooth';
      o.frequency.value = f;
      const lfo = c.ctx.createOscillator();
      lfo.type = 'sine';
      lfo.frequency.value = 5.5 + c.rng();
      const depth = c.ctx.createGain();
      depth.gain.value = f * 0.03;
      lfo.connect(depth);
      depth.connect(o.frequency);
      lfo.start(c.t);
      o.start(c.t);
      o.connect(body);
      sources.push(o, lfo);
    }
    const scream = band(c, 1500, 0.8, 1.7);
    const bp = scream.bp;
    body.connect(bp);
    scream.cap.connect(out);
    const grit = c.ctx.createGain();
    grit.gain.value = 0.16;
    // A grinder wheel has a top end. Without a cap the noise layer runs flat to
    // Nyquist and the whole cue measures at twelve kilohertz — the spectrum of
    // escaping steam rather than of a cutting disc. It also has to stay below
    // the sawtooth body, because a grinder is a scream and not a hiss, and while
    // the hiss was the loud half this measured brighter than the lockpick.
    const hp = filter(c, 'highpass', 2400, 0.7);
    const wheelCap = filter(c, 'lowpass', 3600, 0.7);
    src.connect(hp);
    hp.connect(wheelCap);
    wheelCap.connect(grit);
    grit.connect(out);
    out.gain.setValueAtTime(0.0001, c.t);
    out.gain.linearRampToValueAtTime(0.13, c.t + 0.12);
    setter = (p) => {
      // Brighter and angrier as the cut goes through.
      bp.frequency.value = 1500 + p * 1100;
      scream.cap.frequency.value = (1500 + p * 1100) * 1.7;
      grit.gain.value = 0.16 + p * 0.22;
      wheelCap.frequency.value = 3600 + p * 1200;
    };
  } else if (kind === 'force') {
    // Crowbar. Not continuous — it is a series of heaves, so the loop is a slow
    // tremolo on a low creak, and the creak's filter opens on each heave.
    const creak = band(c, 380, 3.5, 2.4);
    const bp = creak.bp;
    src.connect(bp);
    creak.cap.connect(out);
    const heave = c.ctx.createOscillator();
    heave.type = 'sine';
    heave.frequency.value = 1.15;
    const depth = c.ctx.createGain();
    depth.gain.value = 0.1;
    heave.connect(depth);
    depth.connect(out.gain);
    heave.start(c.t);
    sources.push(heave);
    const wob = c.ctx.createGain();
    wob.gain.value = 260;
    heave.connect(wob);
    wob.connect(bp.frequency);
    out.gain.setValueAtTime(0.0001, c.t);
    out.gain.linearRampToValueAtTime(0.13, c.t + 0.2);
    setter = (p) => {
      heave.frequency.value = 1.15 + p * 0.9;
    };
  } else if (kind === 'pick') {
    // Lock picking. Tiny, dry, close to the ear: tumblers under a tension
    // wrench. The whole point is that it is the quiet option, so it is mixed
    // eight times below the grinder and has no low end at all.
    const tumbler = band(c, 3600, 6, 1.5);
    const bp = tumbler.bp;
    src.connect(bp);
    tumbler.cap.connect(out);
    const chop = c.ctx.createOscillator();
    chop.type = 'square';
    chop.frequency.value = 7;
    const depth = c.ctx.createGain();
    depth.gain.value = 0.022;
    chop.connect(depth);
    depth.connect(out.gain);
    chop.start(c.t);
    sources.push(chop);
    out.gain.setValueAtTime(0.0001, c.t);
    // Six decibels up from where this started. "Quieter than the grinder" was
    // the design and thirty-two decibels down was the result: under the room
    // tone, on the one tool whose entire selling point is that you can hear
    // yourself think while you use it.
    out.gain.linearRampToValueAtTime(0.055, c.t + 0.05);
    setter = (p) => {
      chop.frequency.value = 7 + p * 6;
      bp.frequency.value = 3600 + p * 900;
    };
  } else if (kind === 'card') {
    // A reader thinking about it. Almost nothing, then a decision.
    const o = c.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = 3150;
    o.start(c.t);
    sources.push(o);
    const chop = c.ctx.createOscillator();
    chop.type = 'square';
    chop.frequency.value = 2.6;
    const depth = c.ctx.createGain();
    depth.gain.value = 0.02;
    chop.connect(depth);
    depth.connect(out.gain);
    chop.start(c.t);
    sources.push(chop);
    o.connect(out);
    out.gain.setValueAtTime(0.0001, c.t);
    out.gain.linearRampToValueAtTime(0.035, c.t + 0.05);
  } else if (kind === 'safe') {
    // A drill. Steady, mechanical, and it labours: the pitch drops as the bit
    // bites and the noise band widens as swarf comes off.
    const o = c.ctx.createOscillator();
    o.type = 'square';
    o.frequency.value = 128;
    o.start(c.t);
    sources.push(o);
    // Q here was 2.4, and the resonant peak sitting on a square wave's
    // fundamental made this the densest sound in the game by a factor of three —
    // for twenty-two seconds at a stretch, over the top of the footsteps the
    // player needs to hear while they are stood still and defenceless.
    const lp = filter(c, 'lowpass', 620, 1.3);
    o.connect(lp);
    lp.connect(out);
    // Swarf, low and capped. Twenty-two seconds is the longest anything in this
    // game runs for, so the drill is the one loop that absolutely must not be
    // shrill — and it has to stay clear of the card reader, which measured as
    // the same object at the same brightness.
    const bp = filter(c, 'bandpass', 1500, 1.2);
    const swarfCap = filter(c, 'lowpass', 2600, 0.7);
    const grit = c.ctx.createGain();
    grit.gain.value = 0.12;
    src.connect(bp);
    bp.connect(swarfCap);
    swarfCap.connect(grit);
    grit.connect(out);
    out.gain.setValueAtTime(0.0001, c.t);
    out.gain.linearRampToValueAtTime(0.05, c.t + 0.1);
    setter = (p) => {
      o.frequency.value = 128 - p * 26;
      grit.gain.value = 0.12 + p * 0.22;
    };
  } else if (kind === 'deposit') {
    // Loading a dock. Bags, trolleys, someone else's problem now.
    const bp = filter(c, 'bandpass', 700, 1.1);
    const cap = filter(c, 'lowpass', 2400, 0.7);
    src.connect(bp);
    bp.connect(cap);
    cap.connect(out);
    const shuffle = c.ctx.createOscillator();
    shuffle.type = 'sine';
    shuffle.frequency.value = 2.2;
    const depth = c.ctx.createGain();
    depth.gain.value = 0.05;
    shuffle.connect(depth);
    depth.connect(out.gain);
    shuffle.start(c.t);
    sources.push(shuffle);
    out.gain.setValueAtTime(0.0001, c.t);
    out.gain.linearRampToValueAtTime(0.07, c.t + 0.1);
  }

  return loopHandle(c.ctx, out, sources, setter);
}

/**
 * THE BED. The sound of a shopping centre with nobody in it.
 *
 * Four layers, all noise, all filtered differently, because that is genuinely
 * what the inside of a large empty building sounds like: plant on the roof, air
 * moving through ducts, a fluorescent hum, and a very low rumble from the car
 * park that you feel rather than hear. No layer is interesting on its own and
 * none of them is supposed to be noticed. What they do is give the silence a
 * floor, so that a footstep two rooms away has something to be quieter than.
 *
 * `set` is the zone blend, 0 shop floor to 1 back of house, and it opens the
 * duct layer up and pulls the hum down — which is why walking through the
 * service door sounds like walking through a service door.
 */
export function bed(c: CueCtx): Loop {
  const out = c.ctx.createGain();
  out.gain.value = 0;
  out.connect(c.out);
  const sources: AudioScheduledSourceNode[] = [];

  const layer = (seed: number, type: BiquadFilterType, freq: number, q: number, level: number, cap = 0) => {
    const src = c.ctx.createBufferSource();
    src.buffer = noiseBuffer(c.ctx, 2.0, seed);
    src.loop = true;
    const f = filter(c, type, freq, q);
    const g = c.ctx.createGain();
    g.gain.value = level;
    src.connect(f);
    // See `band`. A bandpassed layer without a rolloff on top is a bright layer,
    // whatever its centre frequency says, and this bed is four of them summed.
    if (cap > 0) {
      const lp = filter(c, 'lowpass', freq * cap, 0.7);
      f.connect(lp);
      lp.connect(g);
    } else {
      f.connect(g);
    }
    g.connect(out);
    src.start(c.t, c.rng() * 1.8);
    sources.push(src);
    return { f, g };
  };

  const rumble = layer(11, 'lowpass', 105, 0.7, 1.6);
  const duct = layer(12, 'bandpass', 340, 0.6, 0.5, 3.0);
  // A band, not an open highpass. Measured as a highpass this layer put the
  // whole bed's centre of energy at eight kilohertz — an empty mall that hissed
  // like a hi-hat, and worse, a hiss sitting exactly where a distant footstep's
  // transient has to be heard.
  const air = layer(13, 'bandpass', 2400, 0.8, 0.05, 2.0);

  // The fluorescent hum. Mains-frequency buzz, and its second harmonic, which is
  // the part the ear actually identifies as "strip lighting".
  const hum = c.ctx.createGain();
  hum.gain.value = 0.014;
  for (const [f, g] of [
    [50, 1],
    [100, 0.55],
    [150, 0.2],
  ]) {
    const o = c.ctx.createOscillator();
    o.type = 'sine';
    o.frequency.value = f;
    const og = c.ctx.createGain();
    og.gain.value = g;
    o.connect(og);
    og.connect(hum);
    o.start(c.t);
    sources.push(o);
  }
  hum.connect(out);

  // Very slow drift on the duct layer, so the bed is never quite the same twice
  // and never sits still enough to be identified as a loop.
  const drift = c.ctx.createOscillator();
  drift.type = 'sine';
  drift.frequency.value = 0.043;
  const driftDepth = c.ctx.createGain();
  driftDepth.gain.value = 90;
  drift.connect(driftDepth);
  driftDepth.connect(duct.f.frequency);
  drift.start(c.t);
  sources.push(drift);

  /**
   * Ten decibels below where this started, and the measurement is why.
   *
   * The bed rendered at four times the RMS of a footstep. Ambience that is
   * louder than the information it is supposed to sit under is not ambience, it
   * is a mask — and in a game where the whole skill is hearing somebody else
   * move, masking is the worst thing an audio system can do. It also spends the
   * master compressor's entire headroom on a sound nobody is listening to, so
   * every cue that matters gets pulled down to make room for the air handling.
   *
   * Two and a half seconds to fade in, because a room tone that arrives is a
   * room tone the player noticed.
   */
  out.gain.setValueAtTime(0.0001, c.t);
  out.gain.linearRampToValueAtTime(0.16, c.t + 2.5);

  return loopHandle(c.ctx, out, sources, (back: number) => {
    duct.g.gain.value = 0.5 + back * 0.9;
    duct.f.Q.value = 0.6 + back * 0.5;
    hum.gain.value = 0.014 * (1 - back * 0.55);
    air.g.gain.value = 0.05 * (1 - back * 0.7);
    rumble.g.gain.value = 1.6 + back * 0.5;
  });
}

// ---------------------------------------------------------------------------

/** Every one-shot, by name. What `tools/listen.ts` walks and what the director calls. */
export const ONE_SHOTS = {
  step: (c: CueCtx) => step(c, { surface: 'tile', effort: 0.7 }),
  stepConcrete: (c: CueCtx) => step(c, { surface: 'concrete', effort: 0.7 }),
  stepAsphalt: (c: CueCtx) => step(c, { surface: 'asphalt', effort: 0.7 }),
  stepCreep: (c: CueCtx) => step(c, { surface: 'tile', effort: 0.15 }),
  stepGuard: (c: CueCtx) => step(c, { surface: 'tile', effort: 0.8, boot: true }),
  land,
  cloth: (c: CueCtx) => cloth(c),
  take,
  drop,
  breach,
  shutter,
  chute,
  bank: (c: CueCtx) => bank(c),
  spotted,
  caught,
  alarm,
  radio: (c: CueCtx) => radio(c),
  guardDown,
  shove,
  doorsOpen,
  tick: (c: CueCtx) => tick(c),
  tickUrgent: (c: CueCtx) => tick(c, true),
  roundEnd,
  uiTick,
  refused,
} satisfies Record<string, (c: CueCtx) => number>;

export type CueId = keyof typeof ONE_SHOTS;
